/**
 * Conversation Store
 * 管理会话列表，提供给 /messages 页面使用
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import * as conversationUtils from '@/indexdb/chat/conversationUtils'
import { getConversationList } from '@/api/chat'
import { isStrangerConversation } from '@/utils/chat'
import { STRANGER_CONVERSATION, CONVERSATION_TYPE } from '@/common/constants'
import { groupBy } from 'lodash'
// import { formatConversations } from '@/utils/conversation'

export const useConversationStore = defineStore('conversation', () => {

  // ========== State ==========
  const conversations = ref([]) // ConversationBean 对象数组
  const conversationsGroup = ref({})
  const isLoadingConversations = ref(false)
  const hasMoreConversations = ref(true)
  // const pageSize = ref(20)
  const conversationError = ref(null)

  // ========== Computed - 分类后的会话列表 ==========

  /**
   * 陌生人会话列表（实时计算，纯陌生人，不含特殊会话）
   */
  const strangerConversations = computed(() => conversationsGroup.value.stranger || [])

  /**
   * 普通会话列表（包含陌生人聚合项）
   */
  const normalConversations = computed(() => conversationsGroup.value.normal || [])

  // ========== Actions ==========

  /**
   * 初始化会话列表 - 从缓存加载，然后同步API ✅
   */
  async function initConversations() {
    isLoadingConversations.value = true
    conversationError.value = null

    try {
      // const userId = userStore.getId()

      // console.log('[Conversation Store] initConversations - userId:', userId)

      // 步骤 1: 从 IndexedDB 缓存加载（快速显示）
      const cachedConversations = await conversationUtils.getAllConversations()

      if (!cachedConversations || !cachedConversations.length) return

      // 保存原始数据
      conversations.value = cachedConversations

      // 步骤 2: 分类陌生人消息
      const group = groupBy(cachedConversations, conv => {
        // 实时计算是否为陌生人会话
        return isStrangerConversation(conv) ? CONVERSATION_TYPE.STRANGER : CONVERSATION_TYPE.NORMAL
      })
      const { normal: normalList = [], stranger: strangerList = [] } = group
      let latestStrangerConv, strangerConvUnreadCount = 0
      if (strangerList?.length) {
        // 按时间排序，获取最新的陌生人会话，因为 getAllConversations 返回的已经是根据时间排序好了的，所以直接取第一个
        latestStrangerConv = strangerList[0]

        // 计算总未读数
        strangerConvUnreadCount = strangerList.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0)
      }
      // 创建陌生人聚合项
      const strangerAggregation = {
        ...STRANGER_CONVERSATION,
        preview: latestStrangerConv
          ? `${latestStrangerConv.target_nick_name || 'Unknown'}: ${latestStrangerConv.preview || ''}`
          : STRANGER_CONVERSATION.preview,
        last_msg_at: latestStrangerConv?.last_msg_at || 0,
        unreadCount: strangerConvUnreadCount,
      }

      // 将陌生人聚合项插入到列表开头
      normalList.unshift(strangerAggregation)

      // 步骤 4: 对前4个特殊会话（3个系统会话 + 1个陌生人聚合）按 last_msg_at 排序
      // 只需要排序前4个，因为它们的 rank=1000，一定在普通会话之前

      const specialConversations = []
      normalList.some(i => {
        if (i.is_special) {
          specialConversations.push(i)
          return false
        }
        return true
      })
      const specialConversationsCount = specialConversations.length
      if (specialConversationsCount > 0) {
        // 按 last_msg_at 降序排序
        specialConversations.sort((a, b) => {
          const timeA = a.last_msg_at || 0
          const timeB = b.last_msg_at || 0
          return timeB - timeA
        })
      }
      // 步骤 5: 保存分类结果
      conversationsGroup.value = {
        normal: normalList.toSpliced(0, specialConversationsCount, ...specialConversations),
        stranger: strangerList
      }
    } catch (error) {
      conversationError.value = error.message
    } finally {
      isLoadingConversations.value = false
    }
  }

  /**
   * 从 API 刷新会话列表（首次登录 + 下拉刷新）
   * 获取最新会话详情，选择性更新 IndexedDB（排除某些字段）
   * @param {string} type - 刷新类型：CONVERSATION_TYPE.NORMAL | CONVERSATION_TYPE.STRANGER | undefined（全部）
   * @returns {Promise<void>}
   */
  async function refreshConversationsFromAPI(type) {
    try {
      // 1. 根据类型获取对应的会话 ID
      let sourceConversations = []

      if (type === CONVERSATION_TYPE.NORMAL) {
        // 普通会话：从 normalConversations 中排除特殊会话
        sourceConversations = normalConversations.value.filter(conv => !conv.is_special)
      } else if (type === CONVERSATION_TYPE.STRANGER) {
        // 陌生人会话：直接使用 strangerConversations
        sourceConversations = strangerConversations.value
      } else {
        // 未指定类型：刷新所有非特殊会话
        sourceConversations = conversations.value.filter(conv => !conv.is_special)
      }

      const conversationIds = sourceConversations.map(conv => conv.conversation_id)

      if (conversationIds.length === 0) {
        console.log('[Conversation Store] No conversations to refresh for type:', type)
        return
      }

      // 2. 调用 API 获取最新会话详情
      const response = await getConversationList({
        conversation_ids: conversationIds
      })

      const { list = [] } = response

      if (list.length === 0) {
        console.log('[Conversation Store] No conversations returned from API')
        return
      }

      // 3. 选择性更新 IndexedDB（排除 last_msg_at, target_read_at 等字段）
      await conversationUtils.updateConversationsSelectively(
        list,
        ['created_at', 'last_msg_at', 'target_read_at', 'member_read_at', 'unreadCount', 'preview']
      )

      // 4. 刷新列表显示
      await initConversations()

      console.log('[Conversation Store] Conversations refreshed from API:', list.length, 'type:', type)
    } catch (error) {
      console.error('[Conversation Store] Failed to refresh conversations from API:', error)
      throw error
    }
  }

  /**
   * 更新会话已读时间（立即更新本地，不等待接口） ✅
   * @param {string} conversationId - 会话ID
   * @param {number} readAt - 已读时间戳
   */
  async function updateConversationReadTime(conversationId, readAt) {
    try {
      // 更新 IndexedDB
      await conversationUtils.updateConversation(conversationId, {
        target_read_at: readAt,
        unreadCount: 0
      })

      // 更新本地状态
      const conversation = conversations.value.find(c => c.conversation_id === conversationId)
      if (conversation) {
        conversation.target_read_at = readAt
        conversation.unreadCount = 0
      }

      // console.log('[Conversation Store] Updated read time locally:', conversationId, readAt)
    } catch (error) {
      // console.error('[Conversation Store] Failed to update read time:', error)
    }
  }

  /**
   * 更新会话的最后消息 ✅
   * @param {string} conversationId - 会话ID
   * @param {Object} updates - 更新的字段
   */
  async function updateConversation(conversationId, updates) {
    try {
      // 更新 IndexedDB
      await conversationUtils.updateConversation(conversationId, updates)

      // 重新加载会话列表以保持排序
      conversations.value = await conversationUtils.getAllConversations()
    } catch (error) {
      // console.error('[Conversation Store] Failed to update conversation:', error)
    }
  }

  /**
   * 删除会话 ✅
   * @param {string} conversationId - 会话ID
   */
  async function deleteConversation(conversationId) {
    try {
      // 从 IndexedDB 删除
      await conversationUtils.deleteConversation(conversationId)

      // 更新本地状态
      conversations.value = conversations.value.filter(c => c.conversation_id !== conversationId)
    } catch (error) {
      console.error('[Conversation Store] Failed to delete conversation:', error)
      throw error
    }
  }

  /**
   * 清空所有会话数据（用户退出登录时调用）✅
   */
  function clearConversations() {
    // console.log('[Conversation Store] Clearing all conversations')
    conversations.value = []
    conversationsGroup.value = {}  // ✅ 同时清空分组数据
    isLoadingConversations.value = false
    hasMoreConversations.value = true
    conversationError.value = null
  }

  /**
   * 获取会话 ✅
   * @param {string} conversationId - 会话ID
   * @returns {Promise<ConversationBean>}
   */
  async function getConversation(conversationId) {
    try {
      const conversation = await conversationUtils.getConversation(conversationId)
      return conversation
    } catch (error) {
      console.error('[Conversation Store] Failed to get conversation by id:', error)
      throw error
    }
  }

  /**
   * 确保特殊会话存在（系统、客服、团队） ✅
   * 如果不存在则创建，始终保证这4个特殊会话在列表中
   */
  // function ensureSpecialConversations(list = []) {
  //   const specialConv = keyBy(SPECIAL_CONVERSATIONS, 'target_uid')
  //   const newList = list.map(item => {
  //     if (specialConv[item.target_uid]) {
  //       const newItem = {
  //         ...item,
  //         ...specialConv[item.target_uid],
  //       }
  //       delete newItem.target_uid
  //       return newItem
  //     } else if (isStrangerConversation(item)) {
  //       const newItem = {
  //         ...item,
  //         is_stranger: true,
  //       }
  //       return newItem
  //     }
  //     return item
  //   })

  //   // 创建缺失的特殊会话 TODO: 还是说有消息来了之后再创建？
  //   for (const convConfig of Object.values(specialConv)) {
  //     newList.push({
  //       ...convConfig,
  //       conversation_type: 'normal',
  //       unreadCount: 0,
  //       chat_depth: 0,
  //       chat_source: 'System',
  //       status: 100,
  //       target_role: 20,
  //       target_sex: 1,
  //       created_at: Date.now(),
  //       member_read_at: Date.now(),
  //       last_msg_at: Date.now(),
  //       updated_at: Date.now(),
  //     })
  //   }
  //   return newList
  // }

  return {
    // State
    conversations,
    normalConversations,      // 🆕 普通会话列表（含聚合项）
    strangerConversations,    // 🆕 陌生人会话列表
    isLoadingConversations,
    hasMoreConversations,
    conversationError,

    // Actions
    initConversations,
    refreshConversationsFromAPI,
    // loadMoreConversations,
    // syncConversationsFromAPI,
    updateConversationReadTime,
    updateConversation,
    getConversation,
    deleteConversation,
    clearConversations,
  }
})
